/**
 * The shouting.
 *
 * The lines are spoken by the browser's own speech synthesis rather than by
 * recorded clips: it keeps the "download nothing" rule, it means a line can be
 * rewritten in dialogue.ts without a trip to a microphone, and every machine at
 * the LAN already has a Finnish voice or can fetch one.
 *
 * Three things about the Web Speech API make this less trivial than it looks:
 *
 *   1. `getVoices()` is empty on first call in Chrome - the list arrives later
 *      with a `voiceschanged` event, so the lookup has to be lazy.
 *   2. An utterance that is not referenced anywhere can be garbage collected
 *      mid-sentence, so the one being spoken is held in a field.
 *   3. Speech is not routed through the AudioContext, so it has no master gain
 *      and no suspend - both are done by hand here.
 */

import { VOICE, type VoiceStyle } from '../config/audio';

export interface VoiceRequest extends VoiceStyle {
  id: string;
  text: string;
}

/**
 * Best available voice for `languages`, best first: an exact language match,
 * then any dialect of the same language, then whatever the browser defaults to.
 * Returns null only when the browser has no voices at all.
 */
export function pickVoice(
  voices: readonly SpeechSynthesisVoice[],
  languages: readonly string[],
): SpeechSynthesisVoice | null {
  const normalise = (lang: string): string => lang.toLowerCase().replace('_', '-');

  for (const wanted of languages.map(normalise)) {
    const exact = voices.find((voice) => normalise(voice.lang) === wanted);
    if (exact) return exact;
  }
  for (const wanted of languages.map(normalise)) {
    const prefix = wanted.split('-')[0];
    const near = voices.find((voice) => {
      const lang = normalise(voice.lang);
      return lang === prefix || lang.startsWith(`${prefix}-`);
    });
    if (near) return near;
  }
  return voices.find((voice) => voice.default) ?? voices[0] ?? null;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** How long a line may take to start speaking before it counts as broken. */
const START_TIMEOUT_MS = 900;

export class Voice {
  private readonly synth: SpeechSynthesis | null;
  private cached: SpeechSynthesisVoice[] | null = null;
  /**
   * The line in the speaker's mouth. The utterance is held here because an
   * unreferenced one can be garbage collected halfway through the sentence.
   */
  private current: { utterance: SpeechSynthesisUtterance; id: string } | null = null;
  private watchdog = 0;

  constructor(synth: SpeechSynthesis | null = globalThis.speechSynthesis ?? null) {
    this.synth = synth;
    this.synth?.addEventListener?.('voiceschanged', this.onVoicesChanged);
  }

  /** False when the browser has no speech synthesis at all. */
  get supported(): boolean {
    return this.synth !== null;
  }

  /** True while a line is being spoken. */
  isSpeaking(id?: string): boolean {
    if (!this.current) return false;
    return id === undefined || this.current.id === id;
  }

  /**
   * Says `request` out loud, interrupting whatever was being said.
   *
   * `onFailure` runs when nothing came out: no speech engine, no voices, or an
   * utterance that never started. The caller answers it with a wordless cue, so
   * the moment is never silent on a machine without Finnish TTS installed.
   */
  say(request: VoiceRequest, onFailure?: () => void): void {
    const synth = this.synth;
    if (!synth) {
      onFailure?.();
      return;
    }
    // Asking for the line already in the speaker's mouth would restart it from
    // the top and stutter; Hannu swipes far more often than he talks.
    if (this.isSpeaking(request.id)) return;

    const voice = pickVoice(this.voices(), VOICE.languages);
    const utterance = new SpeechSynthesisUtterance(request.text);
    if (voice) utterance.voice = voice;
    utterance.lang = voice?.lang ?? VOICE.languages[0];
    utterance.rate = clamp(request.rate ?? VOICE.base.rate, 0.1, 10);
    utterance.pitch = clamp(request.pitch ?? VOICE.base.pitch, 0, 2);
    utterance.volume = clamp(request.volume ?? VOICE.base.volume, 0, 1);

    let timer = 0;
    let settled = false;
    /** Whether this utterance is still the current one, or a newer one won. */
    const mine = (): boolean => this.current?.id === request.id;
    const settle = (failed: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (mine()) this.current = null;
      if (failed) onFailure?.();
    };

    utterance.onstart = () => clearTimeout(timer);
    utterance.onend = () => settle(false);
    // cancel() raises onerror as well; being interrupted is not a failure.
    utterance.onerror = () => settle(mine());

    // Let go of the line being replaced before cancelling it: cancel() raises
    // onerror on it, and being interrupted must not look like a failure.
    // Cancelling first is also the standard workaround for Chrome wedging its
    // queue after a tab has been in the background.
    this.current = null;
    synth.cancel();
    this.current = { utterance, id: request.id };
    // A dead engine (voice list still empty, no network for a remote voice)
    // fails by doing nothing at all, so silence is what has to be watched for.
    timer = setTimeout(() => settle(mine()), START_TIMEOUT_MS);
    this.watchdog = timer;
    synth.speak(utterance);
  }

  cancel(): void {
    clearTimeout(this.watchdog);
    this.current = null;
    this.synth?.cancel();
  }

  setSuspended(suspended: boolean): void {
    if (!this.synth) return;
    if (suspended) this.synth.pause();
    else this.synth.resume();
  }

  dispose(): void {
    this.cancel();
    this.synth?.removeEventListener?.('voiceschanged', this.onVoicesChanged);
  }

  private voices(): SpeechSynthesisVoice[] {
    if (this.cached && this.cached.length > 0) return this.cached;
    this.cached = this.synth?.getVoices() ?? [];
    return this.cached;
  }

  private readonly onVoicesChanged = (): void => {
    this.cached = null;
  };
}
